import classNames from 'classnames';
import { FC, ReactElement } from 'react';
import { ChevronRightIcon } from '@heroicons/react/outline';
import {
  AssessmentQuestionType,
  AssessmentSignatureUser,
} from '../../../generated/graphql';

import Signature from '../../svgs/Signature';
import {
  SignatureUserTypeLabel,
  getColorForSignatureUserType,
} from '../../lib/multiSignature';
import { parseMarkdown } from '../../lib/markdown';
import { getMultiSelectDisplayRows } from '../../lib/answer-option-display';

const QuestionItem: FC<{
  question: string;
  isForm?: boolean;
  questionIndex: number;
  answer?: string;
  subscale?: string | null;
  /** Muted line under the prompt (e.g. contact category, field hints) */
  caption?: string | null;
  answerOptions?: string[];
  /** When set, MultiSelect / choice option rows show checked vs unchecked. */
  selectedAnswers?: string[];
  className?: string;
  answerClassName?: string;
  assessmentSignatureUsers?: AssessmentSignatureUser[];
  additionalAnswerElem?: ReactElement;
  questionType?: AssessmentQuestionType;
}> = ({
  answer,
  question,
  subscale,
  caption,
  questionIndex,
  answerOptions,
  selectedAnswers,
  className,
  answerClassName,
  assessmentSignatureUsers,
  additionalAnswerElem,
  isForm = false,
  questionType,
}) => {
  // Check if subscale indicates Client signature or initials
  const isClientSignatureSubscale =
    subscale &&
    (subscale.toLowerCase().includes('client signature') ||
      subscale.toLowerCase().includes('client initials'));
  const optionRows =
    answerOptions != null
      ? getMultiSelectDisplayRows(answerOptions, selectedAnswers)
      : [];
  const isMultiSelect = questionType === AssessmentQuestionType.MultiSelect;

  return (
    <div
      key={questionIndex}
      className={classNames(
        'flex flex-row items-start justify-start text-caption',
        answer ? 'mb-3' : 'mb-2',
        className,
      )}
    >
      {!isForm && (
        <div className="mr-1 w-6 flex-none text-left font-bold">{`${
          questionIndex + 1
        }.`}</div>
      )}
      <div className="mb-1 flex flex-col items-start justify-start">
        <span className="break-words">
          <div
            className={classNames('prose prose-sm max-w-none text-green-150')}
            dangerouslySetInnerHTML={{ __html: parseMarkdown(question) }}
          />
          {caption ? (
            <div className="mt-1 pb-1.5 text-small-caption text-neutral-125">
              {caption}
            </div>
          ) : null}
          {subscale && (
            <div className="mb-2 flex items-center gap-1">
              {isClientSignatureSubscale && (
                <Signature className="text-neutral-500 h-3 w-3" />
              )}
              <span
                className={`inline-flex items-center justify-between rounded-full px-2 py-1 text-small-label font-normal ${
                  isClientSignatureSubscale
                    ? 'text-neutral-700 bg-blue-25'
                    : 'bg-neutral-50 text-neutral-150'
                }`}
              >
                {subscale}
              </span>
            </div>
          )}
          {assessmentSignatureUsers && (
            <div className="mt-2 flex flex-wrap items-center gap-1">
              <Signature className="text-neutral-500 mr-1 h-3 w-3" />
              {assessmentSignatureUsers
                .sort((a, b) => a.orderIndex - b.orderIndex)
                .map((user, index, array) => {
                  const colorScheme = getColorForSignatureUserType(
                    user.signatureUserType,
                  );
                  return (
                    <div
                      key={user.signatureUserType}
                      className="flex items-center"
                    >
                      <span
                        className={`text-neutral-700 inline-flex items-center justify-between rounded-full px-2 py-1 text-small-label font-normal ${colorScheme.backgroundColor}`}
                      >
                        {SignatureUserTypeLabel[user.signatureUserType]}
                      </span>
                      {index < array.length - 1 && (
                        <ChevronRightIcon className="text-neutral-400 mx-1 h-3 w-3" />
                      )}
                    </div>
                  );
                })}
            </div>
          )}
        </span>
        {optionRows.length > 0 && (
          <div className="mt-2 mb-1 flex flex-col items-start justify-start pl-6">
            {optionRows.map((row, index) => {
              if (row.kind === 'header') {
                return (
                  <div
                    className="mb-1 mt-2 text-caption font-bold text-green-150 first:mt-0"
                    key={index}
                  >
                    {row.label}
                  </div>
                );
              }
              return (
                <div
                  className="mb-0.5 flex flex-row items-start justify-start break-words"
                  key={index}
                >
                  <div
                    className={classNames(
                      'mt-[5px] h-[10px] min-w-[10px] border',
                      isMultiSelect ? 'rounded-sm' : 'rounded-full',
                      row.selected
                        ? 'border-green-150 bg-green-150'
                        : 'border-neutral-125 bg-transparent',
                    )}
                  />
                  <span
                    className={classNames(
                      'ml-3 text-caption font-medium',
                      row.selected ? 'text-green-150' : 'text-neutral-125',
                    )}
                  >
                    {row.label}
                  </span>
                </div>
              );
            })}
          </div>
        )}
        {(answer || additionalAnswerElem) && (
          <div className={classNames('break-words font-bold', answerClassName)}>
            {additionalAnswerElem && <div>{additionalAnswerElem}</div>}
            {answer && (
              <div
                className={classNames(
                  'prose prose-sm max-w-none font-medium text-green-150',
                )}
                dangerouslySetInnerHTML={{ __html: parseMarkdown(answer) }}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionItem;
